import { Globals } from '@/modules/Globals';
import axios from 'axios';

const apiUrl = Globals.apiPath + '/billing';

export type TBillingProvider = 'rebill' | 'stripe';
export type TBillingCheckoutSessionStatus =
  | 'CREATED'
  | 'REDIRECTED'
  | 'CONFIRMED'
  | 'EXPIRED'
  | 'FAILED';

export interface IBillingPlanCatalogItem {
  tier: number;
  subTier: number;
  title: string;
  currency: string;
  variants: Record<number, number>;
}

export interface IBillingPlansResponse {
  restaurantId: number;
  country: string;
  currency: string;
  plans: IBillingPlanCatalogItem[];
  periods: number[];
  provider: TBillingProvider;
}

export interface ICreateBillingCheckoutSessionDto {
  restaurantId: number;
  tier: number;
  subTier?: number | null;
  periodMonths: number;
  email?: string;
}

export interface ICreateBillingCheckoutSessionResponse {
  externalId: string;
  redirectUrl: string;
}

export interface IBillingCheckoutSessionResponse {
  externalId: string;
  restaurantId: number;
  restaurantStringId: string | null;
  provider: TBillingProvider;
  status: TBillingCheckoutSessionStatus;
  createdAt: string;
  confirmedAt: string | null;
  expiresAt: string | null;
  metadata: Record<string, unknown> | null;
  subscriptionStatus: string | null;
  nextBillingAt: string | null;
}

export type TBillingSubscriptionUiStatus =
  | 'NONE'
  | 'ACTIVE'
  | 'PAUSED'
  | 'PAST_DUE'
  | 'CANCELLED'
  | 'EXPIRED'
  | 'UNKNOWN';

export type TBillingRecoveryAction =
  | 'none'
  | 'update_payment_method'
  | 'resubscribe';

export interface IBillingSubscriptionSummaryResponse {
  restaurantId: number;
  provider: TBillingProvider | null;
  status: TBillingSubscriptionUiStatus;
  recoveryAction: TBillingRecoveryAction;
  currentTier: number;
  subscriptionTier: number | null;
  periodMonths: number | null;
  planExpiresAt: string | null;
  nextBillingAt: string | null;
  cancelAtPeriodEnd: boolean;
  pendingConfirmation: boolean;
  renewalPending: boolean;
}

export interface IBillingSubscriptionReconcileResponse {
  summary: IBillingSubscriptionSummaryResponse;
  reconcile: {
    processedSnapshots: number;
    pendingConfirmation: boolean;
    lastProviderStatus: string | null;
  };
}

export interface IBillingCustomerPortalSessionResponse {
  url: string;
}

export default class BillingService {
  async getPlans(restaurantId: number): Promise<IBillingPlansResponse> {
    const res = await axios.get<IBillingPlansResponse>(
      `${apiUrl}/plans?restaurantId=${restaurantId}`
    );
    return res.data;
  }

  async createCheckoutSession(
    dto: ICreateBillingCheckoutSessionDto
  ): Promise<ICreateBillingCheckoutSessionResponse> {
    const res = await axios.post<ICreateBillingCheckoutSessionResponse>(
      `${apiUrl}/checkout-sessions`,
      dto
    );
    return res.data;
  }

  async getCheckoutSession(
    externalId: string
  ): Promise<IBillingCheckoutSessionResponse> {
    const res = await axios.get<IBillingCheckoutSessionResponse>(
      `${apiUrl}/checkout-sessions/${externalId}`
    );
    return res.data;
  }

  async getSubscription(
    restaurantId: number
  ): Promise<IBillingSubscriptionSummaryResponse> {
    const res = await axios.get<IBillingSubscriptionSummaryResponse>(
      `${apiUrl}/subscription?restaurantId=${restaurantId}`
    );
    return res.data;
  }

  async reconcileSubscription(
    restaurantId: number,
    input?: { maxWaitMs?: number }
  ): Promise<IBillingSubscriptionReconcileResponse> {
    const res = await axios.post<IBillingSubscriptionReconcileResponse>(
      `${apiUrl}/subscription/reconcile`,
      { restaurantId, maxWaitMs: input?.maxWaitMs }
    );
    return res.data;
  }

  async cancelSubscription(
    restaurantId: number
  ): Promise<IBillingSubscriptionSummaryResponse> {
    const res = await axios.post<IBillingSubscriptionSummaryResponse>(
      `${apiUrl}/subscription/cancel`,
      { restaurantId }
    );
    return res.data;
  }

  async resumeSubscription(
    restaurantId: number
  ): Promise<IBillingSubscriptionSummaryResponse> {
    const res = await axios.post<IBillingSubscriptionSummaryResponse>(
      `${apiUrl}/subscription/resume`,
      { restaurantId }
    );
    return res.data;
  }

  async changeSubscriptionPlan(input: {
    restaurantId: number;
    tier: number;
    subTier?: number | null;
    periodMonths: number;
  }): Promise<IBillingSubscriptionSummaryResponse> {
    const res = await axios.post<IBillingSubscriptionSummaryResponse>(
      `${apiUrl}/subscription/change-plan`,
      input
    );
    return res.data;
  }

  async createCustomerPortalSession(
    restaurantId: number
  ): Promise<IBillingCustomerPortalSessionResponse> {
    const res = await axios.post<IBillingCustomerPortalSessionResponse>(
      `${apiUrl}/customer-portal`,
      { restaurantId }
    );
    return res.data;
  }
}
